import { useEffect, useState } from "react";
import { Box, Paper } from "@mui/material";
import { IScan, IScanWithId } from "../../models/stat";
import Progress from "../../components/progress/Progress";
import DataTable from "../../components/table/data/DataTable";
import { useRawScansColumns } from "../../hooks/useTableColumns";
import HBScanStatService from "../../services/stat/HBScanStatService";
import moment from "moment";
import IEmployee from "../../models/employee";
import DeepSet from "../../utils/deepSet";
import Toolbar from "../../components/toolbar/Toolbar";
import DownloadDataTable from "../../utils/download/downloadDataTable";
import { IFilter } from "../../components/table/data/filter/DataFilter";
import { useTranslation } from "react-i18next";
import SearchBarPropertyGroups from "../../components/searchbar/SearchBarPropertyGroups";
import useWorkRoute from "./useWorkRoute";
import routeService from "../../services/routeService/RouteService";
import { IRoute } from "../../models/route";

const ScansPage = () => {
  const scansColumns = useRawScansColumns({ hasUserName: true });
  const [scans, setScans] = useState<IScan[]>([]);
  const [isFilterActive, setIsFilterActive] = useState<boolean>(true);
  const { t } = useTranslation();
  const [selectedRoutes, setSelectedRoutes] = useState<number[]>([]);
  const [routes, setRoutes] = useState<IRoute[]>([]);
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);

  const {
    start,
    setStart,
    end,
    setEnd,
    propertyGroupId,
    setPropertyGroupId,
    propertyGroups,
  } = useWorkRoute();

  useEffect(() => {
    if (propertyGroupId <= 0) return;
    routeService.GetLocalRoutesByPropertyGroupId(propertyGroupId).then(setRoutes);
    setSelectedRoutes([])
  }, [propertyGroupId])


  const getSelectedEmployees = async (scans: IScan[]) => {
    const employees: IEmployee[] = new DeepSet(
      scans.map((s) => ({ id: s.userId, name: s.username }))
    ).values();

    setEmployees(employees);
  }

  const getScans = async () => {
    if (!propertyGroupId || propertyGroupId <= 0) return
    if (!start || !end) return

    const scans = await HBScanStatService.getRawScans(
      propertyGroupId,
      start,
      end
    );
    return scans;
  };

  const filterScans = () => {
    if (!scans) return [];
    let filteredScans = scans

    if (selectedEmployees.length) {
      filteredScans = filteredScans.filter((scan) =>
        selectedEmployees.includes(scan.userId)
      );
    }

    if (selectedRoutes.length) {
      const routeLocalIds = routes.filter(r => selectedRoutes.includes(r.id)).flatMap(route => route.locals.map(local => local.id));
      filteredScans = filteredScans.filter((scan) => routeLocalIds.includes(scan.localId));
    }

    const scansWithId = filteredScans.map((scan, index) => ({ ...scan, id: index }));
    scansWithId.sort((a, b) => moment(b.date).diff(moment(a.date)));
    return scansWithId
  }
  const filteredScans: IScanWithId[] = filterScans()

  useEffect(() => {
    getScans()
      .then((scans) => {
        if (!scans) return;
        setScans(scans)
        getSelectedEmployees(scans);
      })
  }, [propertyGroupId, start, end]);

  const [filteredRows, setFilteredRows] = useState<IScanWithId[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<
    Map<string, IFilter>
  >(new Map());

  const visibleFilters = [
    `${t("header.from")} ${moment.utc(start).local().format("LLL")} ${t("header.to")} ${moment.utc(end).local().format("LLL")}`,
    `${propertyGroups.find((b) => b.id === propertyGroupId)?.name}`,
    selectedRoutes.length > 0 ? `${t("table.route.routes")} : ${routes.filter(r => selectedRoutes.includes(r.id)).map(r => r.name)}` : '',
  ];
  selectedFilters.forEach((filter, field) => {
    if (!filter.isActive || !filter.request) return;
    const column = scansColumns.find((column) => column.field === field);
    visibleFilters.push(`${column?.headerName}: ${t(`periodical:filter.${filter.type}`)} ${filter.request}`);
  });

  const downloadTable = new DownloadDataTable("scans", filteredRows, scansColumns, t("table.scans.title"), visibleFilters);

  return (
    <>
      <Toolbar isFilterActive={isFilterActive} onFilterClick={() => setIsFilterActive(!isFilterActive)}
        downloadButtons={[
          { label: "Excel", onClick: () => downloadTable.GetExcel(t("header.exportedBy"), t("header.exportDate")) },
          { label: "CSV", onClick: () => downloadTable.GetCSV() },
          { label: "PDF", onClick: () => downloadTable.GetPDF(t("header.exportedBy"), t("header.exportDate")) }
        ]} />
      {isFilterActive && (
        <SearchBarPropertyGroups
          propertyGroups={propertyGroups}
          setPropertyGroupId={setPropertyGroupId}
          propertyGroupId={propertyGroupId}
          employees={employees}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          start={start}
          setStart={setStart}
          end={end}
          setEnd={setEnd}
          isFilteredByRoute={true}
          selectedRoutes={selectedRoutes}
          setSelectedRoutes={setSelectedRoutes}
          routes={routes}
        />

      )}

      <Box component={Paper} sx={{ width: "100%", marginTop: 2 }}>
        <DataTable
          dense
          disableFiltrer={!isFilterActive}
          rows={filteredScans}
          columns={scansColumns}
          onSetFilter={(filteredRows, selectedFilters) => {
            setFilteredRows(filteredRows)
            setSelectedFilters(selectedFilters)
          }}
        />
      </Box>
    </>
  );
};

export default ScansPage;
